import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  MenuItem, Select, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, Tooltip,
} from '@mui/material';
import IotTeamTab from '../components/settings/IotTeamTab';
const ROLES = [
  { id: 1,    label: 'Admin' },
  { id: 2,    label: 'Super User' },
  { id: 3,    label: 'User' },
  { id: null, label: 'Pending' },
];

const ROLE_COLOR = { admin: 'error', super_user: 'warning', user: 'primary' };

function roleChip(roleName) {
  if (!roleName) return <Chip label="No Role" size="small" />;
  const label = roleName === 'user' ? 'User' : roleName.replace(/_/g, ' ');
  return <Chip label={label} size="small" color={ROLE_COLOR[roleName] || 'default'} />;
}

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

// -- Users & Access tab -------------------------------------------------------

function UserProjectChips({ userId, allProjects, onChanged }) {
  const [userProjects, setUserProjects] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addId, setAddId] = useState('');

  const load = useCallback(() => {
    fetch(`/api/access/users/${userId}/projects`, { headers: bearerHeaders() })
      .then(r => r.json())
      .then(j => setUserProjects(j.projects || []))
      .catch(() => setUserProjects([]));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (projectId) => {
    await fetch(`/api/access/projects/${projectId}/members/${userId}`, {
      method: 'DELETE', headers: bearerHeaders(),
    });
    load();
    if (onChanged) onChanged();
  };

  const add = async () => {
    if (!addId) return;
    await fetch(`/api/access/projects/${addId}/members`, {
      method: 'POST', headers: bearerHeaders(),
      body: JSON.stringify({ userId }),
    });
    setAdding(false);
    setAddId('');
    load();
    if (onChanged) onChanged();
  };

  if (userProjects === null) return <CircularProgress size={14} />;

  const assignedIds = new Set(userProjects.map(p => p.id));
  const available = allProjects.filter(p => !assignedIds.has(p.id));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {userProjects.map(p => (
        <Chip key={p.id} label={p.name} size="small" variant="outlined"
          onDelete={() => remove(p.id)} />
      ))}
      {userProjects.length === 0 && !adding && null}
      {adding ? (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Select size="small" value={addId} onChange={e => setAddId(e.target.value)}
            displayEmpty sx={{ minWidth: 140, fontSize: 12 }}>
            <MenuItem value=""><em>Select project...</em></MenuItem>
            {available.map(p => <MenuItem key={p.id} value={p.id} sx={{ fontSize: 12 }}>{p.name}</MenuItem>)}
          </Select>
          <Button size="small" onClick={add} disabled={!addId}>Add</Button>
          <Button size="small" color="inherit" onClick={() => { setAdding(false); setAddId(''); }}>✕</Button>
        </Stack>
      ) : (
        available.length > 0 && (
          <Tooltip title="Grant project access">
            <Chip label="+ project" size="small" variant="outlined" color="primary"
              onClick={() => setAdding(true)} sx={{ cursor: 'pointer', borderStyle: 'dashed' }} />
          </Tooltip>
        )
      )}
    </Box>
  );
}

function UsersAccessTab() {
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingName, setEditingName] = useState({}); // userId → draft name

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch('/api/access/users').then(r => r.json()),
      fetch('/api/access/projects').then(r => r.json()),
    ])
      .then(([u, p]) => {
        setUsers(u.users || []);
        setAllProjects(p.projects || []);
        setLoading(false);
      })
      .catch(() => { setMsg('Failed to load'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const setRole = async (userId, roleId) => {
    setMsg('');
    try {
      const res = await fetch(`/api/access/users/${userId}/role`, {
        method: 'PUT', headers: bearerHeaders(),
        body: JSON.stringify({ roleId }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || j.error || `HTTP ${res.status}`);
      setMsg('Role updated.');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const saveName = async (userId, name) => {
    setMsg('');
    try {
      const res = await fetch(`/api/access/users/${userId}/name`, {
        method: 'PUT', headers: bearerHeaders(),
        body: JSON.stringify({ name }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setEditingName(e => { const n = { ...e }; delete n[userId]; return n; });
      load();
    } catch (e) { setMsg(e.message); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Remove user ${email}? This deletes all their access grants.`)) return;
    setMsg('');
    try {
      const res = await fetch(`/api/access/users/${userId}`, {
        method: 'DELETE', headers: bearerHeaders(),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || j.error || `HTTP ${res.status}`);
      setMsg('User removed.');
      load();
    } catch (e) { setMsg(e.message); }
  };

  const pendingCount = users.filter(u => !u.role_id).length;
  const displayed = filter === 'pending' ? users.filter(u => !u.role_id) : users;

  if (loading) return <CircularProgress sx={{ m: 3 }} />;

  return (
    <Box>
      {msg && <Typography color={msg.includes('updated') || msg.includes('removed') ? 'success.main' : 'error'} sx={{ mb: 2 }}>{msg}</Typography>}

      <Box sx={{ mb: 2.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>How to give a new user access</Typography>

        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {[
            { n: 1, text: <>Set <b>System Role</b> — choose based on what the user needs to access (see table below).</> },
            { n: 2, text: <>For <b>User</b> role: click <b>+ project</b> in the Project Access column to grant access to specific carbon projects. They can only see projects you assign.</> },
            { n: 3, text: <>For <b>Admin</b> or <b>Super User</b>: they automatically access all IoT devices and all projects — no project grants needed.</> },
            { n: 4, text: <>User logs out and logs back in — they are taken to the correct part of the platform.</> },
          ].map(({ n, text }) => (
            <Stack key={n} direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 18, mt: '1px', color: 'text.primary' }}>{n}.</Typography>
              <Typography variant="caption" color="text.secondary">{text}</Typography>
            </Stack>
          ))}
        </Stack>

        <Table size="small" sx={{ '& td, & th': { fontSize: 11, py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>IoT Devices</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Carbon Projects</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Use for</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { role: 'Admin',      iot: 'All devices + system config', project: 'All projects', use: 'Platform administrator' },
              { role: 'Super User', iot: 'All devices',                  project: 'All projects', use: 'Senior field scientist' },
              { role: 'User',       iot: 'Via IoT grants only',           project: 'Via project grants below', use: 'Farmer, agronomist, consultant' },
              { role: 'Pending',    iot: 'None',                          project: 'None', use: 'New user — set a role to activate' },
            ].map(row => (
              <TableRow key={row.role}>
                <TableCell><b>{row.role}</b></TableCell>
                <TableCell>{row.iot}</TableCell>
                <TableCell>{row.project}</TableCell>
                <TableCell color="text.secondary">{row.use}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} pending`} color="warning" size="small"
            sx={{ cursor: 'pointer' }}
            onClick={() => setFilter(f => f === 'pending' ? 'all' : 'pending')}
            variant={filter === 'pending' ? 'filled' : 'outlined'} />
        )}
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Display Name</TableCell>
            <TableCell sx={{ minWidth: 160 }}>System Role</TableCell>
            <TableCell sx={{ minWidth: 280 }}>Project Access</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {displayed.map(u => {
            const isDraftName = editingName[u.user_id] !== undefined;
            const draftName = isDraftName ? editingName[u.user_id] : (u.user_nick_name || '');
            return (
              <TableRow key={u.user_id}>
                <TableCell sx={{ fontSize: 12 }}>{u.user_email}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      size="small"
                      value={draftName}
                      onChange={e => setEditingName(n => ({ ...n, [u.user_id]: e.target.value }))}
                      onBlur={() => { if (isDraftName) saveName(u.user_id, draftName); }}
                      onKeyDown={e => { if (e.key === 'Enter') saveName(u.user_id, draftName); if (e.key === 'Escape') setEditingName(n => { const x = {...n}; delete x[u.user_id]; return x; }); }}
                      placeholder="Enter name..."
                      inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }}
                      sx={{ width: 140 }}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Select size="small" value={u.role_id ?? ''}
                    onChange={e => setRole(u.user_id, e.target.value === '' ? null : e.target.value)}
                    sx={{ minWidth: 110, fontSize: 12 }}>
                    {ROLES.map(r => (
                      <MenuItem key={r.id ?? 'none'} value={r.id ?? ''} sx={{ fontSize: 13 }}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  {u.role_id && <UserProjectChips userId={u.user_id} allProjects={allProjects} />}
                </TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => deleteUser(u.user_id, u.user_email)}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// -- General Settings tab -----------------------------------------------------

function GeneralTab() {
  const [projects, setProjects] = useState([]);
  const [newProj, setNewProj] = useState({ code: '', name: '' });
  const [editingProj, setEditingProj] = useState({}); // id → {name}
  const [msg, setMsg] = useState('');

  const loadProjects = useCallback(() => {
    fetch('/api/access/projects').then(r => r.json()).then(j => setProjects(j.projects || [])).catch(() => {});
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const createProject = async () => {
    setMsg('');
    try {
      const res = await fetch('/api/access/projects', {
        method: 'POST', headers: bearerHeaders(), body: JSON.stringify(newProj),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setNewProj({ code: '', name: '' });
      setMsg('Project created.');
      loadProjects();
    } catch (e) { setMsg(e.message); }
  };

  const renameProject = async (id) => {
    const draft = editingProj[id];
    if (!draft?.name?.trim()) return;
    setMsg('');
    try {
      const res = await fetch(`/api/access/projects/${id}`, {
        method: 'PUT', headers: bearerHeaders(), body: JSON.stringify({ name: draft.name.trim() }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setEditingProj(e => { const n = {...e}; delete n[id]; return n; });
      setMsg('Renamed.');
      loadProjects();
    } catch (e) { setMsg(e.message); }
  };

  const deleteProject = async (proj) => {
    const confirm1 = window.prompt(`This will permanently delete "${proj.name}" and all its sites, team assignments, and data.\n\nType the project name to confirm:`);
    if (confirm1 === null) return;
    if (confirm1.trim() !== proj.name) { setMsg('Name did not match — delete cancelled.'); return; }
    setMsg('');
    try {
      const res = await fetch(`/api/access/projects/${proj.id}`, {
        method: 'DELETE', headers: bearerHeaders(),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.ok === false) throw new Error(j.message || `HTTP ${res.status}`);
      setMsg(`"${proj.name}" deleted.`);
      loadProjects();
    } catch (e) { setMsg(e.message); }
  };

  return (
    <Stack spacing={3}>
      {msg && <Typography variant="body2" color={msg.toLowerCase().includes('error') || msg.toLowerCase().includes('cancel') ? 'error' : 'success.main'}>{msg}</Typography>}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Projects</Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Sites</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map(p => {
                const isDraft = editingProj[p.id] !== undefined;
                const draftName = isDraft ? editingProj[p.id].name : p.name;
                return (
                  <TableRow key={p.id}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{p.code}</TableCell>
                    <TableCell>
                      <TextField size="small" value={draftName}
                        onChange={e => setEditingProj(d => ({ ...d, [p.id]: { name: e.target.value } }))}
                        onBlur={() => { if (isDraft) renameProject(p.id); }}
                        onKeyDown={e => { if (e.key === 'Enter') renameProject(p.id); if (e.key === 'Escape') setEditingProj(d => { const n={...d}; delete n[p.id]; return n; }); }}
                        inputProps={{ style: { fontSize: 13 } }} sx={{ minWidth: 200 }} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{p.sites?.length ?? 0} sites</TableCell>
                    <TableCell>
                      <Button size="small" color="error" onClick={() => deleteProject(p)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {projects.length === 0 && (
                <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.disabled">No projects yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Add New Project</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" label="Code (e.g. SANTA)" value={newProj.code}
              onChange={e => setNewProj(p => ({ ...p, code: e.target.value }))} sx={{ width: 160 }} />
            <TextField size="small" label="Name" value={newProj.name}
              onChange={e => setNewProj(p => ({ ...p, name: e.target.value }))} sx={{ width: 240 }} />
            <Button variant="contained" size="small" onClick={createProject}
              disabled={!newProj.code.trim() || !newProj.name.trim()}>Create</Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

// -- Main page -----------------------------------------------------------------

export default function SettingsPage() {
  const [tab, setTab] = useState(0);

  return (
    <>
      <Helmet><title>Settings | TERRRA</title></Helmet>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 3 }}>Settings</Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="General" />
          <Tab label="Users & Access" />
          <Tab label="IoT Team" />
        </Tabs>
        {tab === 0 && <GeneralTab />}
        {tab === 1 && <UsersAccessTab />}
        {tab === 2 && <IotTeamTab />}
      </Container>
    </>
  );
}
